import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { pick } from '../pick';

const toNullable = value => {
  if (value === '' || value === undefined) return null;
  return value;
};

const resolveCargoRatioNumber = cargoRatio => {
  const numeric = Number(cargoRatio);
  return Number.isFinite(numeric) ? numeric : cargoRatio;
};

export const normalizeCargoRatioPricingFields = (entry = {}) => ({
  id: pick(entry, 'id', 'Id'),
  quantityInformationFK: pick(
    entry,
    'quantityInformationFK',
    'QuantityInformationFK'
  ),
  quantityIncotermFK: pick(entry, 'quantityIncotermFK', 'QuantityIncotermFK'),
  quantityInformationCargoRatioFK: pick(
    entry,
    'quantityInformationCargoRatioFK',
    'QuantityInformationCargoRatioFK'
  ),
  quantityInformationCounterFK: pick(
    entry,
    'quantityInformationCounterFK',
    'QuantityInformationCounterFK'
  ),
  quantityIncotermCounterFK: pick(
    entry,
    'quantityIncotermCounterFK',
    'QuantityIncotermCounterFK'
  ),
  cargoRatio: pick(entry, 'cargoRatio', 'CargoRatio'),
  cargoRatioLabel: pick(entry, 'cargoRatioLabel', 'CargoRatioLabel'),
  parcelSize: pick(entry, 'parcelSize', 'ParcelSize') ?? null,
  alpha: pick(entry, 'alpha', 'Alpha') ?? null,
  alphaImprovement: pick(entry, 'alphaImprovement', 'AlphaImprovement') ?? null,
  alphaHps: pick(entry, 'alphaHps', 'AlphaHps') ?? null,
  alphaDyedHps: pick(entry, 'alphaDyedHps', 'AlphaDyedHps') ?? null,
  alphaUndyedHps: pick(entry, 'alphaUndyedHps', 'AlphaUndyedHps') ?? null,
});

const pickPatchOrExisting = (patch, existing, key) => {
  if (Object.prototype.hasOwnProperty.call(patch, key)) {
    return toNullable(patch[key]);
  }
  return toNullable(existing?.[key]);
};

const resolveIdentityField = (patch, existing, camelKey, altKey) => {
  if (Object.prototype.hasOwnProperty.call(patch, camelKey)) {
    return toNullable(patch[camelKey]);
  }
  if (altKey && Object.prototype.hasOwnProperty.call(patch, altKey)) {
    return toNullable(patch[altKey]);
  }
  return toNullable(existing?.[camelKey] ?? existing?.[altKey]);
};

export const buildCargoRatioPutBody = (existing = {}, patch = {}) => ({
  quantityInformationFK: resolveIdentityField(
    patch,
    existing,
    'quantityInformationFK',
    'quantityInformationId'
  ),
  quantityIncotermFK: resolveIdentityField(
    patch,
    existing,
    'quantityIncotermFK',
    'quantityIncotermId'
  ),
  cargoRatio: resolveCargoRatioNumber(
    pickPatchOrExisting(patch, existing, 'cargoRatio')
  ),
  parcelSize: pickPatchOrExisting(patch, existing, 'parcelSize'),
  alpha: pickPatchOrExisting(patch, existing, 'alpha'),
  alphaImprovement: pickPatchOrExisting(patch, existing, 'alphaImprovement'),
  alphaHps: pickPatchOrExisting(patch, existing, 'alphaHps'),
  alphaDyedHps: pickPatchOrExisting(patch, existing, 'alphaDyedHps'),
  alphaUndyedHps: pickPatchOrExisting(patch, existing, 'alphaUndyedHps'),
});

export const buildCargoRatioCounterPutBody = (existing = {}, patch = {}) => ({
  quantityInformationCargoRatioFK: pickPatchOrExisting(
    patch,
    existing,
    'quantityInformationCargoRatioFK'
  ),
  quantityInformationCounterFK: pickPatchOrExisting(
    patch,
    existing,
    'quantityInformationCounterFK'
  ),
  quantityIncotermCounterFK: pickPatchOrExisting(
    patch,
    existing,
    'quantityIncotermCounterFK'
  ),
  cargoRatio: resolveCargoRatioNumber(
    pickPatchOrExisting(patch, existing, 'cargoRatio')
  ),
  parcelSize: pickPatchOrExisting(patch, existing, 'parcelSize'),
  alpha: pickPatchOrExisting(patch, existing, 'alpha'),
  alphaImprovement: pickPatchOrExisting(patch, existing, 'alphaImprovement'),
  alphaHps: pickPatchOrExisting(patch, existing, 'alphaHps'),
  alphaDyedHps: pickPatchOrExisting(patch, existing, 'alphaDyedHps'),
  alphaUndyedHps: pickPatchOrExisting(patch, existing, 'alphaUndyedHps'),
});

export const resolveCargoRatioExistingFromRow = (row = {}) => {
  const snapshot =
    row.cargoRatioSnapshot ??
    row.cargoRatioExisting ??
    row.counterCargoRatioSnapshot ??
    null;
  if (snapshot && typeof snapshot === 'object') {
    return normalizeCargoRatioPricingFields(snapshot);
  }

  return normalizeCargoRatioPricingFields({
    id: row.quantityInformationCargoRatioId ?? row.counterCargoRatioRecordId,
    quantityInformationFK: row.quantityInformationId,
    quantityIncotermFK: row.quantityIncotermId,
    quantityInformationCargoRatioFK:
      row.quantityInformationCargoRatioId ??
      row.quantityInformationCargoRatioFK,
    quantityInformationCounterFK: row.quantityInformationCounterId,
    quantityIncotermCounterFK: row.quantityIncotermCounterId,
    cargoRatio: row.counterCargoRatioId ?? row.cargoRatio,
    parcelSize: row.cargoRatioParcelSize ?? row.parcelSize,
    alpha: row.cargoRatioAlpha ?? row.alpha ?? row.counterAlpha,
    alphaImprovement:
      row.cargoRatioAlphaImprovement ??
      row.alphaImprovement ??
      row.improvementAlpha ??
      null,
    alphaHps: row.alphaHps ?? row.cargoRatioAlphaHps ?? null,
    alphaDyedHps: row.alphaDyedHps ?? row.cargoRatioAlphaDyedHps ?? null,
    alphaUndyedHps: row.alphaUndyedHps ?? row.cargoRatioAlphaUndyedHps ?? null,
  });
};

export const putCargoRatioPricing = async ({
  quantityInformationCargoRatioId,
  existing = {},
  patch = {},
  token = null,
} = {}) => {
  const authToken = token || getAuthToken();
  const recordId =
    quantityInformationCargoRatioId ?? existing?.id ?? patch?.id ?? null;
  if (!authToken || !recordId) return null;

  const body = buildCargoRatioPutBody(existing, patch);
  if (
    body.quantityInformationFK === null ||
    body.quantityInformationFK === undefined ||
    body.quantityInformationFK === ''
  ) {
    return null;
  }

  const response = await fetch(
    `${api.API_URL}/quantity-information-cargo-ratio/${recordId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update cargo ratio pricing (${response.status})`
    );
  }

  return response.json();
};

export const putCargoRatioCounterPricing = async ({
  quantityInformationCargoRatioCounterId,
  existing = {},
  patch = {},
  token = null,
} = {}) => {
  const authToken = token || getAuthToken();
  const recordId =
    quantityInformationCargoRatioCounterId ?? existing?.id ?? patch?.id ?? null;
  if (!authToken || !recordId) return null;

  const body = buildCargoRatioCounterPutBody(existing, patch);

  const response = await fetch(
    `${api.API_URL}/quantity-information-cargo-ratio-counter/${recordId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update cargo ratio counter pricing (${response.status})`
    );
  }

  return response.json();
};

export const isCargoRatioImprovementRow = (detailRow = {}, kind = 'offer') => {
  if (kind === 'counter') {
    return !!(
      detailRow?.counterCargoRatioRecordId ||
      detailRow?.quantityInformationCargoRatioCounterId
    );
  }
  return !!detailRow?.quantityInformationCargoRatioId;
};
